import {
  Avatar,
  Box,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LinkReturnToMain } from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useImposter, useIsDesktop } from 'Hooks';
import { PurpleSearch, PurpleSearchIcon, PurpleX } from 'Images';
import { useEffect, useRef, useState } from 'react';
import { ResultItem } from './ResultItem';
import { sendEvent } from 'Utils/sendEvent';
import {
  IActivityCategory,
  getActivities,
  getFileSource,
} from 'API/ActivitiesLobby/activitiesLobby';
import { ACTIVITY_TYPE } from 'Enum/ActivitiesLobby';
import { useNavigate } from 'react-router-dom';
import { downloadFile } from 'Utils';
import {
  eshetToursId,
  flyingCarpetId,
} from 'Modules/ActivitiesLobby/Activities/ActivityItem';
import { getSupplierRedirectCode } from 'API/Treatment/treatment';

export interface IResult {
  label: string;
  onClick?: () => void;
  isDownload?: boolean;
  link?: string;
}

interface IActivitiesSearch {
  placeholder?: string;
  source?: string;
}

const minSearchLength = 3,
  maxSearchResults = 7;

export const ActivitiesSearchBar = ({
  placeholder,
  source,
}: IActivitiesSearch) => {
  const [isActive, setIsActive] = useState(false);
  const [value, setValue] = useState('');
  const [results, setResults] = useState<IResult[]>([]);
  const [activitiesList, setActivitiesList] = useState<IActivityCategory[]>([]);
  const [popularSuggestions, setPopularSuggestions] = useState<IResult[]>([]);

  const theme = useTheme();
  const isScreenXl = useMediaQuery(theme.breakpoints.up('xl'));

  const navigate = useNavigate();

  const onActivityClick = async ({
    id,
    activityTypeId,
    link,
    label,
    analyticsData,
  }: {
    id: number;
    activityTypeId: ACTIVITY_TYPE;
    link: string;
    label?: string;
    analyticsData?: {
      componentName?: string;
      eventName?: string;
      label?: string;
    };
  }) => {
    sendEvent(
      analyticsData?.componentName || '',
      analyticsData?.eventName || '',
      analyticsData?.label || '',
      ''
    );
    switch (activityTypeId) {
      case ACTIVITY_TYPE.FILE:
        const fileSource = await getFileSource(link);
        if (fileSource) downloadFile(fileSource, '.pdf', label || '', false);
        break;

      case ACTIVITY_TYPE.INTERNAL:
        navigate(link);
        break;

      case ACTIVITY_TYPE.EXTERNAL:
        if (id === flyingCarpetId || id === eshetToursId) {
          const code = await getSupplierRedirectCode(
            id === flyingCarpetId ? 1 : 2
          );
          window.open(
            `https://${
              id === flyingCarpetId ? 'flying-shikum.co.il' : 'shikum.eshet.com'
            }/${code ? '?code=' + encodeURIComponent(code) : ''}`,
            '_blank'
          );
        } else window.open(link, '_blank');
        break;
    }
  };

  const initActivities = async () => {
    const activities = await getActivities();

    if (activities) {
      setPopularSuggestions([
        {
          label: activities[2].activities[0].label,
          onClick: () =>
            onActivityClick({
              id: activities[2].activities[0].id,
              activityTypeId: activities[2].activities[0].activityTypeId,
              link: activities[2].activities[0].link,
              analyticsData: activities[2].activities[0].analyticsData,
            }),
        },
        {
          label: activities[5].activities[0].label,
          onClick: () =>
            onActivityClick({
              id: activities[5].activities[0].id,
              activityTypeId: activities[5].activities[0].activityTypeId,
              link: activities[5].activities[0].link,
              analyticsData: activities[5].activities[0].analyticsData,
            }),
        },
        {
          label: activities[2].activities[2].label,
          onClick: () =>
            onActivityClick({
              id: activities[2].activities[2].id,
              activityTypeId: activities[2].activities[2].activityTypeId,
              link: activities[2].activities[2].link,
              analyticsData: activities[2].activities[2].analyticsData,
            }),
        },
      ]);
      setActivitiesList(activities);
    }
  };

  const { fakeUser } = useImposter();

  useEffect(() => {
    initActivities();
  }, [fakeUser]);

  const dictionary = useDictionary(Dictionary, DICTIONARY.ActivitiesLobby);
  const isDesktop = useIsDesktop();

  const inputRef = useRef<HTMLElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => {
      if (isActive && inputRef.current) inputRef.current.focus();
    }, 0);
  }, [isActive]);

  useEffect(() => {
    const newResults: IResult[] = [];
    activitiesList.forEach((activity) => {
      activity.activities.forEach((item) => {
        [
          ...(item.searchWords.map((word) => word.text) || []),
          item.label,
        ].every((word) => {
          let isFound: boolean = word
            .toLowerCase()
            .includes(value.toLocaleLowerCase());
          if (isFound) {
            newResults.push({
              label: item.label,
              onClick: () =>
                onActivityClick({
                  id: item.id,
                  activityTypeId: item.activityTypeId,
                  link: item.link,
                  label: item.label,
                  analyticsData: item.analyticsData,
                }),
              isDownload: item.activityTypeId === ACTIVITY_TYPE.FILE,
            });
          }
          return !isFound;
        });
      });
    });

    newResults.sort(({ label: a }, { label: b }) => {
      return a.localeCompare(b);
    });

    // sort the list so that any item label that starts with 'פנייה' will be at the end of the list
    newResults.sort((a, b) => {
      if (
        a.label.startsWith(dictionary.application) &&
        !b.label.startsWith(dictionary.application)
      ) {
        return 1;
      } else if (
        !a.label.startsWith(dictionary.application) &&
        b.label.startsWith(dictionary.application)
      ) {
        return -1;
      }
      return 0;
    });

    setResults(newResults);
  }, [value]);

  const searchBarRef = useRef<HTMLDivElement>(null);

  const onCloseModal = () => {
    setIsActive(false);
    setTimeout(() => {
      if (searchBarRef.current) searchBarRef.current.focus();
    }, 0);
  };

  return (
    <>
      {!isActive && (
        <Stack
          ref={searchBarRef}
          component={'button'}
          sx={{
            width: isDesktop ? '564px' : '100%',
            height: '56px',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: isDesktop ? 'flex-start' : 'space-between',
            bgcolor: 'white',
            border: '1px solid',
            borderColor: 'primary.shade_200',
            borderRadius: '30px',
            mt: '24px',
            cursor: 'text',
            p: isDesktop ? undefined : '6px 6px 6px 16px',
            gap: isDesktop ? '10px' : undefined,
            '&:focus': { outlineColor: 'rgb(21, 82, 232)' },
          }}
          onClick={() => {
            if (source === 'home') {
              sendEvent('Search Page', 'HomePageSearch');
            } else if (source === 'activities') {
              sendEvent('Activities Page', 'ActionsLobbySearch');
            }
            setIsActive(true);
          }}
        >
          {isDesktop ? (
            <>
              <Avatar src={PurpleSearch} alt='' />
              <Typography variant={isDesktop ? 'body3' : 'body5'}>
                {placeholder || dictionary.searchButton}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant={'body3'}>
                {placeholder || dictionary.searchButton}
              </Typography>
              <Avatar src={PurpleSearchIcon} alt='' />
            </>
          )}
        </Stack>
      )}

      <Modal
        open={isActive}
        sx={{
          position: isDesktop ? 'absolute' : 'fixed',
          bgcolor: isDesktop ? undefined : 'primary.shade_50',
          right: 0,
          top: isDesktop ? 0 : '72px',
          height: isDesktop ? undefined : 'calc(100vh - 72px)',
          opacity: isActive ? 1 : 0,
          zIndex: isActive ? 99999 : -99999,
          overflow: isDesktop ? undefined : 'hidden',
          direction: 'ltr',
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCloseModal();
        }}
        BackdropProps={{ onClick: () => onCloseModal() }}
      >
        <Stack
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            width: isDesktop && isScreenXl ? 'calc(100vw - 306px)' : '100vw',
            p: isDesktop ? '32px 64px 48px' : '20px 24px 88px',
            bgcolor: 'primary.shade_50',
            zIndex: isActive ? 99999 : -99999,
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            overflow: isDesktop ? 'hidden' : 'auto',
            opacity: isActive ? 1 : 0,
            transition: isDesktop ? 'opacity .5s' : undefined,
            maxHeight: isDesktop ? undefined : 'calc(100vh - 72px)',
            '& ::-webkit-scrollbar': {
              width:
                value.length >= minSearchLength &&
                results.length > maxSearchResults
                  ? '6px'
                  : undefined,
            },
          }}
        >
          {isActive && (
            <LinkReturnToMain
              label={dictionary.back}
              onClick={() => onCloseModal()}
              tabIndex={0}
            />
          )}
          <Stack
            sx={{
              width: isDesktop ? '564px' : undefined,
              height: '56px',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: isDesktop ? 'space-between' : undefined,
              bgcolor: 'white',
              border: '1px solid',
              borderColor: 'primary.shade_200',
              borderRadius: '30px',
              mt: '24px',
              cursor: 'text',
            }}
            onClick={() => {
              setIsActive(true);
            }}
          >
            <Avatar sx={{ ml: '4px' }} src={PurpleSearch} alt='' />
            {isActive && (
              <fieldset>
                <legend>{dictionary.searchButton}</legend>
                <TextField
                  inputRef={inputRef}
                  sx={{
                    width: isDesktop ? '450px' : undefined,
                    ml: isDesktop ? undefined : '5px',
                  }}
                  inputProps={{
                    style: {
                      padding: 0,
                    },
                    'aria-label': placeholder || dictionary.searchButton,
                  }}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
              </fieldset>
            )}
            <IconButton
              sx={{ mr: '12px', ml: isDesktop ? undefined : 'auto' }}
              onClick={() => setValue('')}
              tabIndex={isActive ? undefined : -1}
            >
              <Avatar
                sx={{ width: '20px', height: '20px', cursor: 'pointer' }}
                src={PurpleX}
                alt={Dictionary[DICTIONARY.General].clearSearch}
                aria-label={Dictionary[DICTIONARY.General].clearSearch}
              />
            </IconButton>
          </Stack>
          {value.length >= minSearchLength && results.length === 0 && (
            <>
              <Typography variant='h3' mt='48px'>
                {dictionary.noResults.title}
              </Typography>
              <Typography variant='body3' mt='8px'>
                {dictionary.noResults.subtitle}
              </Typography>
            </>
          )}
          <Typography component={'h2'} variant='h3' mt='48px'>
            {value.length < minSearchLength || results.length === 0
              ? dictionary.popularSuggestions
              : dictionary.whichActivity}
          </Typography>
          {isActive && (
            <Stack
              mt='24px'
              gap='16px'
              maxHeight={
                value.length >= minSearchLength &&
                results.length > maxSearchResults
                  ? '300px'
                  : undefined
              }
              overflow={
                value.length >= minSearchLength &&
                results.length > maxSearchResults
                  ? 'auto'
                  : undefined
              }
            >
              {value.length < minSearchLength || results.length === 0
                ? popularSuggestions.map((item, index: number) => (
                    <ResultItem
                      key={index}
                      item={{ label: item.label, onClick: item.onClick }}
                    />
                  ))
                : results.map((item, index: number) => (
                    <ResultItem key={index} item={item} />
                  ))}
            </Stack>
          )}
        </Stack>
      </Modal>
      <Box
        sx={{
          position: 'fixed',
          width: '100vw',
          height: '100vh',
          top: '80px',
          right: 0,
          bgcolor: 'secondary.dark',
          opacity: isActive ? 0.8 : 0,
          zIndex: isActive ? 9999 : -9999,
          transition: isDesktop
            ? isActive
              ? 'opacity .5s'
              : undefined
            : undefined,
        }}
        onClick={() => onCloseModal()}
      />
    </>
  );
};
